import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { api, type Me } from '../api';
import { MODULE, META, MODULE_ORDER, meta, moduleHref, type ModuleKey } from '../module';
import { Raven } from './Raven';

/**
 * Navigasi sama persis di setiap modul — ux-draft.md 0. Berpindah
 * dari pm. ke eng. tidak boleh terasa seperti berpindah aplikasi;
 * itulah seluruh alasan ini satu platform dan bukan lima.
 *
 * Modul yang belum dibangun tampil redup dan tidak bisa diklik, bukan
 * disembunyikan. Menu yang menjanjikan sesuatu lalu membuka 404
 * merusak kepercayaan jauh lebih parah daripada "Coming soon" yang
 * jujur.
 *
 * ---------------------------------------------------------------
 * KENAPA SAMPING, BUKAN ATAS
 *
 * Menu ini dulu satu baris di atas. Dengan dua modul itu masuk akal.
 * Dengan SEPULUH ia membungkus ke baris kedua dan ketiga, mendorong
 * isi halaman ke bawah dengan tinggi yang berubah-ubah tergantung
 * lebar jendela — dan tinggi header yang tidak bisa ditebak adalah
 * tinggi yang tidak bisa dijadikan sandaran tata letak.
 *
 * Yang lebih penting: sempitnya ruang mendatar memaksa singkatan.
 * "Prog", "Cx", "Proc" adalah kompromi terhadap lebar layar, bukan
 * nama yang dipakai orang. Kolom samping punya ruang untuk nama
 * sebenarnya — "Progress Assurance", "Commissioning" — dan menu yang
 * memakai nama sebenarnya tidak perlu dihafal.
 * ---------------------------------------------------------------
 */

/**
 * Diturunkan dari META, bukan daftar kedua yang ditulis tangan.
 * Menu yang menyimpan salinan daftar modulnya sendiri akan menyimpang
 * begitu satu modul ditambahkan — begitulah tab "Calc" hidup lebih
 * lama daripada modulnya.
 */
const MODULES = MODULE_ORDER.map((key) => ({
  key,
  label: META[key].title,
  live: META[key].live,
}));

export function Shell({
  me,
  inboxCount,
  preview,
  signedIn,
  children,
}: {
  me: Me;
  inboxCount: number;
  /**
   * Pratinjau mempertahankan rangkanya dan melucuti senjatanya. Tautan
   * modul membawa pratinjau menyeberang subdomain; tampilan yang butuh
   * data nyata diredupkan, bukan disembunyikan, supaya menunya tetap
   * terbaca sebagai menu dan bukan sebagai produk lain yang lebih kecil.
   */
  preview?: boolean;
  /**
   * Apakah ada sesi sungguhan di belakang ini. Sengaja dipisah dari
   * `preview`: orang yang sudah masuk lalu membuka modul yang belum
   * dibangun mendapat menu yang dilucuti DAN tombol keluarnya sendiri.
   * Menawarinya "Sign in" saat ia sudah masuk terbaca seperti bug.
   */
  signedIn?: boolean;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const route = useLocation();
  const [drawer, setDrawer] = useState(false);

  /**
   * Laci ditutup setiap kali rutenya berpindah.
   *
   * Di layar sempit menu menutupi isi halaman. Membiarkannya terbuka
   * setelah orang memilih sesuatu berarti menyembunyikan hal yang
   * baru saja mereka minta.
   */
  useEffect(() => setDrawer(false), [route.pathname]);

  /** Escape menutup laci — jalan keluar yang sama untuk setiap lapisan
   *  yang menutupi layar. */
  useEffect(() => {
    if (!drawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawer(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawer]);

  async function signOut() {
    try {
      await api.post('/auth/logout');
    } finally {
      // Sekalipun panggilannya gagal, keluarkan orangnya dari tampilan
      // yang butuh sesi. Tombol keluar yang tampak tidak bekerja lebih
      // buruk daripada tombol keluar yang optimistis.
      location.href = '/';
    }
  }

  const initials = me.user.name
    .split(' ')
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const views = preview
    ? [
        { to: '/', label: 'Dashboard', live: true },
        { to: '#register', label: 'Register', live: false },
      ]
    : meta.views.map((v) => ({ ...v, live: true }));

  return (
    <div className={`shell${drawer ? ' shell-drawer-open' : ''}`}>
      {/* Hanya terlihat di layar sempit; di layar lebar kolomnya
          sudah permanen dan tombol ini tidak punya pekerjaan. */}
      <div className="topbar">
        <button
          className="hamburger"
          onClick={() => setDrawer((v) => !v)}
          aria-label={drawer ? 'Tutup menu' : 'Buka menu'}
          aria-expanded={drawer}
        >
          <span aria-hidden="true" />
        </button>
        <span className="topbar-title">{meta.title}</span>
        <button
          className="bell"
          onClick={() => navigate('/inbox')}
          aria-label={`Kotak masuk, ${inboxCount} menunggu Anda`}
        >
          <span aria-hidden="true">🔔</span>
          {inboxCount > 0 && <b>{inboxCount}</b>}
        </button>
      </div>

      {/* Latar gelap di belakang laci. Mengklik di luar menu adalah
          cara orang menutup menu sebelum mereka mencari tombolnya. */}
      <div className="drawer-scrim" onClick={() => setDrawer(false)} aria-hidden="true" />

      <aside className="side" aria-label="Navigasi utama">
        <a className="side-brand" href={moduleHref('PM', preview ? '/preview' : '/')}>
          <span className="brand-mark" aria-hidden="true">
            <Raven />
          </span>
          <span className="side-brand-name">
            Munin<span className="side-brand-tld"> Partners</span>
          </span>
        </a>

        <div className="side-company" title={me.company.name}>
          {me.company.name}
        </div>

        {/* Tampilan modul ini didahulukan. Yang paling sering ditekan
            orang adalah pindah halaman di dalam modul yang sedang
            dibuka, bukan pindah modul. */}
        {views.length > 0 && (
          <nav className="side-group" aria-label="Halaman">
            <h6>{meta.title}</h6>
            {views.map((v) =>
              v.live ? (
                <NavLink
                  key={v.to}
                  to={v.to}
                  end={v.to === '/'}
                  className={({ isActive }) => `side-link${isActive ? ' on' : ''}`}
                >
                  {v.label}
                </NavLink>
              ) : (
                <span key={v.to} className="side-link off" title="Butuh proyek dan sesi">
                  {v.label}
                </span>
              ),
            )}
          </nav>
        )}

        <nav className="side-group" aria-label="Modul">
          <h6>Modul</h6>
          {MODULES.map((m) => {
            // Seluruh guna pratinjau justru modul yang belum ada, jadi
            // di sana tidak ada yang diredupkan sampai tak terjangkau.
            if (!m.live && !preview) {
              return (
                <span key={m.key} className="side-link off" title="Belum dibangun" aria-disabled="true">
                  {m.label}
                </span>
              );
            }
            if (m.key === MODULE) {
              return (
                <span key={m.key} className="side-link on" aria-current="page">
                  {m.label}
                </span>
              );
            }
            // Modul lain hidup di subdomain lain, jadi ini navigasi
            // sungguhan, bukan rute sisi-klien.
            return (
              <a
                key={m.key}
                className="side-link"
                href={moduleHref(m.key as ModuleKey, preview ? '/preview' : '/')}
              >
                {m.label}
              </a>
            );
          })}
        </nav>

        <div className="side-foot">
          <button
            className="side-link side-inbox"
            onClick={() => navigate('/inbox')}
            aria-label={`Kotak masuk, ${inboxCount} menunggu Anda`}
          >
            <span aria-hidden="true">🔔</span>
            Kotak masuk
            {inboxCount > 0 && <b className="side-count">{inboxCount}</b>}
          </button>

          <div className="side-user">
            <div className="avatar" aria-hidden="true">
              {initials}
            </div>
            <div className="side-user-text">
              <b>{me.user.name}</b>
              <span title={me.user.email}>{me.user.email}</span>
            </div>
          </div>

          {preview && !signedIn ? (
            <a className="btn btn-primary btn-sm side-action" href="/">
              Masuk
            </a>
          ) : (
            <button className="btn btn-ghost btn-sm side-action" onClick={signOut}>
              Keluar
            </button>
          )}
        </div>
      </aside>

      <main className="shell-main">
        <div className="wrap page">{children}</div>
      </main>
    </div>
  );
}
